import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Edit, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

export default function AdminPages() {
  const [pages,   setPages]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/pages')
      .then(r => setPages(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (slug, title) => {
    if (!window.confirm(`Fshi faqen "${title}"?`)) return;
    try {
      await api.delete(`/admin/pages/${slug}`);
      toast.success('Faqja u fshi.');
      load();
    } catch { toast.error('Gabim gjatë fshirjes.'); }
  };

  // Quick toggle show_in_nav
  const toggleNav = async (page) => {
    try {
      await api.put(`/admin/pages/${page.slug}`, {
        title:       page.title,
        content:     page.content || '',
        meta_desc:   page.meta_desc || '',
        sort_order:  page.sort_order || 0,
        show_in_nav: page.show_in_nav ? '0' : '1',
        is_active:   page.is_active ? '1' : '0',
      });
      toast.success(page.show_in_nav ? 'U hoq nga menyja.' : 'U shtua në meny.');
      load();
    } catch { toast.error('Gabim.'); }
  };

  // Move up/down — change sort_order
  const moveOrder = async (page, direction) => {
    const newOrder = Math.max(0, (page.sort_order || 0) + direction * 10);
    try {
      await api.put(`/admin/pages/${page.slug}`, {
        title:       page.title,
        content:     page.content || '',
        meta_desc:   page.meta_desc || '',
        sort_order:  newOrder,
        show_in_nav: page.show_in_nav ? '1' : '0',
        is_active:   page.is_active ? '1' : '0',
      });
      load();
    } catch { toast.error('Gabim.'); }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="page-title">Faqet</h1>
          <p className="admin-page-sub">{pages.length} faqe gjithsej</p>
        </div>
        <Link to="/admin/faqet/i-re" className="btn btn-primary">
          <Plus size={15}/> Faqe e re
        </Link>
      </div>

      <div className="card" style={{marginBottom:'1rem',padding:'1rem 1.5rem',background:'var(--blue-50)',border:'1px solid var(--blue-100)'}}>
        <p style={{fontSize:'0.82rem',color:'var(--blue-800)'}}>
          💡 <strong>Renditja në meny:</strong> Ndryshoni numrin e <strong>Renditjes</strong> tek çdo faqe — faqja me numër më të vogël del e para. 
          Klikoni <strong>👁</strong> për ta treguar/fshehur nga menyja pa e fshirë.
        </p>
      </div>

      {loading ? <div className="spinner"/> : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Titulli</th>
                  <th style={{textAlign:'center'}}>Renditja</th>
                  <th style={{textAlign:'center'}}>Në meny</th>
                  <th style={{textAlign:'center'}}>Aktive</th>
                  <th>Përditësuar</th>
                  <th>Veprimet</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(page => (
                  <tr key={page.id}>
                    <td>
                      <div>
                        <p style={{fontWeight:600,fontSize:'0.875rem',color:'var(--gray-900)'}}>{page.title}</p>
                        <code style={{fontSize:'0.7rem',color:'var(--gray-400)'}}>/faqe/{page.slug}</code>
                      </div>
                    </td>
                    <td style={{textAlign:'center'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4}}>
                        <button className="btn btn-secondary btn-sm" style={{padding:'3px 6px',minWidth:0}}
                          onClick={() => moveOrder(page, -1)} title="Lëviz lart">
                          <ArrowUp size={12}/>
                        </button>
                        <span style={{fontSize:'0.82rem',fontWeight:700,minWidth:28,textAlign:'center'}}>
                          {page.sort_order || 0}
                        </span>
                        <button className="btn btn-secondary btn-sm" style={{padding:'3px 6px',minWidth:0}}
                          onClick={() => moveOrder(page, 1)} title="Lëviz poshtë">
                          <ArrowDown size={12}/>
                        </button>
                      </div>
                    </td>
                    <td style={{textAlign:'center'}}>
                      <button
                        className={`btn btn-sm ${page.show_in_nav ? 'btn-primary' : 'btn-secondary'}`}
                        style={{padding:'4px 10px'}}
                        onClick={() => toggleNav(page)}
                        title={page.show_in_nav ? 'Hiq nga menyja' : 'Shto në meny'}
                      >
                        {page.show_in_nav ? <Eye size={13}/> : <EyeOff size={13}/>}
                        <span style={{marginLeft:4,fontSize:'0.72rem'}}>
                          {page.show_in_nav ? 'Po' : 'Jo'}
                        </span>
                      </button>
                    </td>
                    <td style={{textAlign:'center'}}>
                      <span className={`badge ${page.is_active ? 'status-published' : 'status-archived'}`}>
                        {page.is_active ? 'Aktive' : 'Joaktive'}
                      </span>
                    </td>
                    <td style={{fontSize:'0.78rem',color:'var(--gray-500)'}}>
                      {new Date(page.updated_at).toLocaleDateString('sq-AL')}
                    </td>
                    <td>
                      <div className="action-btns">
                        <a href={`/faqe/${page.slug}`} target="_blank" rel="noreferrer"
                          className="btn btn-secondary btn-sm">
                          <Eye size={13}/>
                        </a>
                        <Link to={`/admin/faqet/ndrysho/${page.slug}`}
                          className="btn btn-secondary btn-sm">
                          <Edit size={13}/>
                        </Link>
                        <button className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(page.slug, page.title)}>
                          <Trash2 size={13}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pages.length === 0 && (
                  <tr><td colSpan={6} style={{textAlign:'center',color:'var(--gray-400)',padding:'2rem'}}>
                    Nuk ka faqe. Krijoni faqen e parë!
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
